import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';

import {
  SupplierFullDtoIn,
  SupplierTaxApprovalStatus,
  isSupplierTaxApprovalActionable,
} from '@/models/in/SupplierFullDtoIn';
import DataTable from '@/components/ui/DataTable/DataTable';
import { Col } from '@/components/ui/DataTable/ColumnsHelpers';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { openSupplierTaxApprovalDocument } from '@/helpers/openSupplierTaxApprovalDocument';
import Button from '@/components/ui/Button';
import ToggleSwitch from '@/components/ui/ToggleSwitch/ToggleSwitch';
import useTableDensity from '@/hooks/useTableDensity';
import EditSupplierBasicDetailsDialog from './EditSupplierBasicDetailsDialog';
import EditSupplierTaxWithholdingDialog from './EditSupplierTaxWithholdingDialog';

const QUERY_URL = 'management/suppliers';
const DENSITY_STORAGE_KEY = 'table.density.suppliersManagement';

const getTaxApprovalStatusBadge = (
  status: SupplierTaxApprovalStatus,
  t: (k: string) => string,
): { text: string; className: string } => {
  switch (status) {
    case SupplierTaxApprovalStatus.Valid:
      return { text: t('suppliersManagement.taxApprovalStatus.Valid'), className: 'text-green-600 font-semibold' };
    case SupplierTaxApprovalStatus.PendingValidUntil:
      return { text: t('suppliersManagement.taxApprovalStatus.PendingValidUntil'), className: 'text-amber-600 font-semibold' };
    case SupplierTaxApprovalStatus.PendingDocument:
      return { text: t('suppliersManagement.taxApprovalStatus.PendingDocument'), className: 'text-amber-600 font-semibold' };
    case SupplierTaxApprovalStatus.Expired:
      return { text: t('suppliersManagement.taxApprovalStatus.Expired'), className: 'text-red-600 font-semibold' };
    case SupplierTaxApprovalStatus.NotRequired:
    default:
      return { text: t('suppliersManagement.taxApprovalStatus.NotRequired'), className: 'text-gray-500' };
  }
};

const formatDate = (dateStr?: string | null): string => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('he-IL');
};

const SuppliersManagementPage = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [supplierToEdit, setSupplierToEdit] = useState<SupplierFullDtoIn | null>(null);
  const [taxDialogOpen, setTaxDialogOpen] = useState(false);
  const [supplierForTax, setSupplierForTax] = useState<SupplierFullDtoIn | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [density, setDensity] = useTableDensity(DENSITY_STORAGE_KEY, 'normal');
  const [showOnlyActionable, setShowOnlyActionable] = useState(false);

  const { data: suppliers, isLoading, refetch } = useGetAuthApi<SupplierFullDtoIn[]>(QUERY_URL);

  const actionableCount = useMemo(
    () => (suppliers ?? []).filter((s) => isSupplierTaxApprovalActionable(s.taxApprovalStatus)).length,
    [suppliers],
  );

  const handleOpenCreateDialog = useCallback(() => {
    setSupplierToEdit(null);
    setEditDialogOpen(true);
  }, []);

  const handleOpenEditDialog = useCallback((supplier: SupplierFullDtoIn) => {
    setSupplierToEdit(supplier);
    setEditDialogOpen(true);
  }, []);

  const handleCloseEditDialog = useCallback(() => {
    setEditDialogOpen(false);
    setSupplierToEdit(null);
  }, []);

  const handleOpenTaxDialog = useCallback((supplier: SupplierFullDtoIn) => {
    setSupplierForTax(supplier);
    setTaxDialogOpen(true);
  }, []);

  const handleCloseTaxDialog = useCallback(() => {
    setTaxDialogOpen(false);
    setSupplierForTax(null);
  }, []);

  const handleViewDocument = useCallback(
    async (supplierId: number) => {
      await openSupplierTaxApprovalDocument(supplierId, currentUser?.token);
    },
    [currentUser?.token],
  );

  const filteredSuppliers = useMemo(() => {
    let list = suppliers ?? [];
    if (showOnlyActionable) {
      list = list.filter((s) => isSupplierTaxApprovalActionable(s.taxApprovalStatus));
    }
    const q = searchQuery.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (s) =>
        (s.name || '').toLowerCase().includes(q) ||
        (s.taxId || '').toLowerCase().includes(q) ||
        (s.phone || '').toLowerCase().includes(q) ||
        (s.email || '').toLowerCase().includes(q),
    );
  }, [suppliers, searchQuery, showOnlyActionable]);

  const columns: ColumnDef<SupplierFullDtoIn>[] = [
    {
      id: 'name',
      header: Col(t('suppliersManagement.name')),
      accessorKey: 'name',
      enableSorting: true,
      meta: { className: 'min-w-[180px]' },
    },
    {
      id: 'taxId',
      header: Col(t('suppliersManagement.taxId')),
      accessorKey: 'taxId',
      enableSorting: true,
      meta: { className: 'min-w-[120px]' },
    },
    {
      id: 'funds',
      header: Col(t('suppliersManagement.funds')),
      enableSorting: false,
      cell: ({ row }) => {
        const funds = row.original.funds ?? [];
        if (funds.length === 0) return <span className="text-gray-500">-</span>;
        const ids = funds.map((f) => f.id).join(', ');
        const tooltip = funds.map((f) => `${f.id} - ${f.name}`).join(', ');
        return (
          <span className="line-clamp-2" title={tooltip}>
            {ids}
          </span>
        );
      },
      meta: { className: 'min-w-[180px] max-w-[260px]' },
    },
    {
      id: 'taxWithholdingPercentage',
      header: Col(t('suppliersManagement.taxWithholdingPercentage')),
      enableSorting: true,
      accessorFn: (row) => row.taxWithholdingPercentage ?? 0,
      cell: ({ row }) => {
        const pct = row.original.taxWithholdingPercentage;
        // Only null means "not configured" - 0 is a real configured value that
        // still requires the approval flow (valid-until + document), so render
        // it explicitly as 0.00% rather than the "-" placeholder.
        if (pct == null) return <span className="text-gray-500">-</span>;
        return <span>{Number(pct).toFixed(2)}%</span>;
      },
      meta: { className: 'min-w-[110px] max-w-[130px]' },
    },
    {
      id: 'taxApprovalValidUntil',
      header: Col(t('suppliersManagement.taxApprovalValidUntil')),
      enableSorting: true,
      accessorFn: (row) => row.taxApprovalValidUntil ?? '',
      cell: ({ row }) => {
        const hasDoc = row.original.taxApprovalDocumentId != null;
        return (
          <div className="flex items-center gap-2">
            <span>{formatDate(row.original.taxApprovalValidUntil)}</span>
            {hasDoc && (
              <button
                type="button"
                className="body-sm-regular text-(--font-primary) underline hover:opacity-80"
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewDocument(row.original.id);
                }}
              >
                {t('suppliersManagement.viewDocument')}
              </button>
            )}
          </div>
        );
      },
      meta: { className: 'min-w-[180px] max-w-[220px]' },
    },
    {
      id: 'taxApprovalStatus',
      header: Col(t('suppliersManagement.taxApprovalStatus.label')),
      enableSorting: true,
      accessorFn: (row) => row.taxApprovalStatus,
      cell: ({ row }) => {
        const { text, className } = getTaxApprovalStatusBadge(row.original.taxApprovalStatus, t);
        return <span className={className}>{text}</span>;
      },
      meta: { className: 'min-w-[140px] max-w-[160px]' },
    },
    {
      id: 'actions',
      header: Col(t('suppliersManagement.actions')),
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenEditDialog(row.original);
            }}
          >
            {t('suppliersManagement.edit')}
          </Button>
          <Button
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenTaxDialog(row.original);
            }}
          >
            {t('suppliersManagement.manageTaxWithholding')}
          </Button>
        </div>
      ),
      meta: { className: 'min-w-[260px]', lockLayout: true },
    },
  ];

  return (
    <div>
      <DataTable
        title={t('suppliersManagement.title')}
        data={filteredSuppliers}
        isLoading={isLoading}
        columns={columns}
        filters={
          <div className="flex items-center gap-4 flex-wrap">
            <Button variant="primary" onClick={handleOpenCreateDialog}>
              {t('suppliersManagement.addSupplier')}
            </Button>
            <ToggleSwitch
              label={
                actionableCount > 0
                  ? `${t('suppliersManagement.filterActionable')} (${actionableCount})`
                  : t('suppliersManagement.filterActionable')
              }
              checked={showOnlyActionable}
              onChange={setShowOnlyActionable}
            />
          </div>
        }
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        density={density}
        onDensityChange={setDensity}
        columnLayoutKey="suppliersManagement"
        searchPlaceholder="suppliersManagement.search"
        customGlobalSearch={() => true}
      />

      <EditSupplierBasicDetailsDialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        supplierToEdit={supplierToEdit}
        onSuccess={refetch}
      />

      {taxDialogOpen && supplierForTax && (
        <EditSupplierTaxWithholdingDialog
          open={taxDialogOpen}
          onClose={handleCloseTaxDialog}
          supplier={supplierForTax}
          onSuccess={refetch}
        />
      )}
    </div>
  );
};

export default SuppliersManagementPage;
